// import { fetchLogoutTimeout } from '@/api'
import apiAuth from '@/api/modules/auth'
import apiUser from '@/api/modules/user'
import router from '@/router'
import { getAliyunDeviceToken } from '@/utils/aliyunDeviceRisk'
import { syncEchoAuthToken } from '@/utils/echo'
import { toast } from 'vue-sonner'

interface RoleInfo {
  is_super_admin: boolean
  is_group_leader: boolean
  is_department_manager: boolean
  is_operation: boolean
  is_supplier: boolean
  is_standalone_station_assistant: boolean
  is_finance: boolean
}

export const useUserStore = defineStore(
  'user',
  () => {
    const settingsStore = useSettingsStore()
    const routeStore = useRouteStore()
    const menuStore = useMenuStore()
    // const tabbarStore = useTabbarStore()

    const account = ref(localStorage.account ?? '')
    const token = ref(localStorage.token ?? '')
    const avatar = ref(localStorage.avatar ?? '')
    const user_identifier = ref(localStorage.user_identifier ?? '')
    const email = ref(localStorage.email ?? '')
    const permissions = ref<string[]>([])
    const isLogin = computed(() => !!token.value)
    const isAdmin = ref(JSON.parse(localStorage.getItem('isAdmin') || 'false'))
    const isLicensee = ref(false)
    const roleInfo = ref<RoleInfo | null>(null)
    const user_id = ref(localStorage.user_id ?? '')
    const tour = ref(localStorage.getItem('tour') === 'true')

    let tokenRefreshTimer: NodeJS.Timeout | null = null

    // 角色判断 getters
    const isSuperAdmin = computed(() => !!roleInfo.value?.is_super_admin)
    const isGroupLeader = computed(() => !!roleInfo.value?.is_group_leader)
    const isDepartmentManager = computed(() => !!roleInfo.value?.is_department_manager)
    const isOperation = computed(() => !!roleInfo.value?.is_operation)
    const isSupplier = computed(() => !!roleInfo.value?.is_supplier)
    const isStandaloneStationAssistant = computed(() => !!roleInfo.value?.is_standalone_station_assistant)
    const isFinance = computed(() => !!roleInfo.value?.is_finance)

    // function startTokenRefreshTimer() {
    //   if (tokenRefreshTimer) {
    //     clearInterval(tokenRefreshTimer)
    //   }

    //   tokenRefreshTimer = setInterval(() => {
    //     const expireTime = Number(localStorage.getItem('tokenExpireTime') ?? 0)
    //     const remaining = expireTime - Date.now()

    //     if (remaining <= 180000) {
    //       refreshToken()
    //     }
    //   }, 60000)
    // }

    async function refreshToken() {
      try {
        const res = await apiAuth.refresh()
        if (res.status !== 1) {
          throw new Error('Token刷新失败')
        }
        token.value = res.data.token
        localStorage.setItem('token', res.data.token)
        syncEchoAuthToken(res.data.token)
        window.dispatchEvent(new CustomEvent('echo:token-refreshed', {
          detail: {
            token: res.data.token,
          },
        }))
        const expiresIn = res.data.expires_in || 3600
        const newExpireTime = Date.now() + expiresIn * 1000
        localStorage.setItem('tokenExpireTime', newExpireTime.toString())
        // startTokenRefreshTimer()
      }
      catch (error) {
        console.error('Token刷新失败，即将登出', error)
        toast.error('登录状态已过期，请重新登录')
        throw error
      }
    }

    async function login(data: {
      account: string
      password: string
    }) {
      try {
        const deviceToken = await getAliyunDeviceToken()
        const res = await apiAuth.login({
          username: data.account,
          password: data.password,
          device_token: deviceToken,
        })

        if (res.status === 1) {
          // 存储用户信息，但主要依赖HttpOnly Cookie进行身份验证
          localStorage.setItem('account', data.account)
          localStorage.setItem('user_id', res.data.user_id)
          localStorage.setItem('token', res.data.token) // 保留token以兼容旧API
          localStorage.setItem('avatar', res.data.avatar || '')
          localStorage.setItem('user_identifier', res.data.user_identifier || '')
          // 邮箱存储
          localStorage.setItem('email', res.data.email)
          // 记录登录时间
          localStorage.setItem('loginTime', Date.now().toString())
          const tourFlag = res.data.is_first_login === 1
          if (tourFlag) {
            localStorage.setItem('tour', 'true')
          }
          else {
            localStorage.removeItem('tour')
          }

          // 存储过期时间和刷新时间（expires_in 为单位：秒）
          const expiresIn = res.data.expires_in || 1800 // 默认 30 分钟有效
          const expireTime = Date.now() + expiresIn * 1000
          localStorage.setItem('tokenExpireTime', expireTime.toString())

          // 更新状态
          account.value = data.account
          user_id.value = res.data.user_id
          token.value = res.data.token
          avatar.value = res.data.avatar || ''
          user_identifier.value = res.data.user_identifier || ''
          email.value = res.data.email
          tour.value = tourFlag
          syncEchoAuthToken(res.data.token)

          // fetchLogoutTimeout()
          // startTokenRefreshTimer()
          checkAdmin()
        }
        else {
          toast.error(res.data.error || 'login failed.')
        }
      }
      catch (error: any) {
        console.error('登录失败', error)
        toast.error(error.message || 'login failed.')
      }
    }

    // 手动登出
    function logout(redirect = router.currentRoute.value.fullPath) {
      window.dispatchEvent(new Event('echo:logout'))
      // 此处仅清除计算属性 isLogin 中判断登录状态过期的变量，以保证在弹出登录窗口模式下页面展示依旧正常
      localStorage.removeItem('token')
      localStorage.removeItem('user_id')
      localStorage.removeItem('tour')
      token.value = ''
      user_id.value = ''
      tour.value = false
      router.push({
        name: 'login',
        query: {
          ...(redirect !== settingsStore.settings.home.fullPath
            && router.currentRoute.value.name !== 'login' && { redirect }),
        },
      }).then(logoutCleanStatus)
    }
    // 请求登出
    async function requestLogout() {
      // 如果当前不是登录状态，则无需请求
      if (!isLogin.value) {
        console.warn('当前不是登录状态，无需请求登出')
        return
      }

      try {
        // 调用登出API，服务端会清除HttpOnly Cookie
        await apiAuth.logout()
      }
      catch (error) {
        console.error('登出请求失败', error)
      }

      window.dispatchEvent(new Event('echo:logout'))
      localStorage.removeItem('token')
      localStorage.removeItem('lastActivityTime')
      localStorage.removeItem('loginTime')
      localStorage.removeItem('autoLogoutTimeout') // 清除登出时间设置缓存
      localStorage.removeItem('tokenExpireTime') // 清除过期时间设置缓存
      localStorage.removeItem('tour')
      token.value = ''
      tour.value = false
      router.push({
        name: 'login',
        query: {
          ...(router.currentRoute.value.fullPath !== settingsStore.settings.home.fullPath
            && router.currentRoute.value.name !== 'login' && {
            redirect: router.currentRoute.value.fullPath,
          }),
        },
      }).then(logoutCleanStatus)
    }

    function logoutCleanStatus() {
      if (tokenRefreshTimer) {
        clearInterval(tokenRefreshTimer)
        tokenRefreshTimer = null
      }
      localStorage.removeItem('account')
      localStorage.removeItem('user_id')
      localStorage.removeItem('avatar')
      localStorage.removeItem('user_identifier')
      localStorage.removeItem('tour')
      account.value = ''
      user_id.value = ''
      avatar.value = ''
      email.value = ''
      user_identifier.value = ''
      tour.value = false
      permissions.value = []
      settingsStore.updateSettings({}, true)
      // tabbarStore.clean()
      routeStore.removeRoutes()
      menuStore.setActived(0)
    }

    // 获取权限
    async function getPermissions() {
      const res = await apiUser.permission()
      permissions.value = res.data.permissions
    }
    // 修改密码
    async function editPassword(data: {
      password: string
      newPassword: string
    }) {
      await apiUser.passwordEdit(data)
    }

    // 更新用户信息
    async function updateUserInfo(data: { avatar?: string, email?: string }) {
      try {
        // 更新成功后，更新本地存储和响应式状态
        if (data.avatar !== undefined) {
          localStorage.setItem('avatar', data.avatar)
          avatar.value = data.avatar
        }

        if (data.email !== undefined) {
          localStorage.setItem('email', data.email)
          email.value = data.email
        }
      }
      catch (error: any) {
        console.error('更新用户信息失败', error)
      }
    }

    // 页面刷新时恢复定时器
    // if (token.value && Number(localStorage.getItem('tokenExpireTime') ?? 0) > Date.now()) {
    //   startTokenRefreshTimer()
    // }

    // 获取是否为超级管理员
    async function checkAdmin() {
      try {
        const res = await apiUser.isAdmin()
        isAdmin.value = res.data.is_super_admin
        localStorage.setItem('isAdmin', JSON.stringify(res.data.is_super_admin))
      }
      catch (error: any) {
        console.error('获取是否为超级管理员失败', error)
      }
    }

    // 获取用户角色信息
    async function getRoleInfo() {
      try {
        const res = await apiUser.roleInfo()
        roleInfo.value = res.data
      }
      catch (error: any) {
        console.error('获取用户角色信息失败', error)
      }
    }

    function finishTour() {
      tour.value = false
      localStorage.removeItem('tour')
    }

    return {
      account,
      user_id,
      token,
      avatar,
      user_identifier,
      email,
      tour,
      permissions,
      isLogin,
      isAdmin,
      isLicensee,
      roleInfo,
      isSuperAdmin,
      isGroupLeader,
      isDepartmentManager,
      isOperation,
      isSupplier,
      isStandaloneStationAssistant,
      isFinance,
      login,
      logout,
      requestLogout,
      getPermissions,
      editPassword,
      updateUserInfo, // 添加更新用户信息的方法
      refreshToken, // 刷新token的方法
      getRoleInfo, // 获取用户角色信息的方法
      finishTour,
    }
  },
)
